<script>
import { mapActions } from 'vuex'

export default {
  name: 'ArticleSocialStep',
  components: {
    Upload: () => import('@/components/general/Upload'),
    FormSection: () => import('@/components/general/FormSection'),
    InputField: () => import('@/components/general/InputField'),
    Radio: () => import('@/components/general/Radio'),
    Datatable: () => import('@/components/general/Datatable'),
    Pagination: () => import('@/components/general/Pagination'),
    Divisor: () => import('@/components/general/Divisor'),
    Action: () => import('@/components/general/Action'),
    Icon: () => import('@/components/general/Icon')
  },

  props: {
    formData: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      searchQuery: '',
      publishedArticles: [],
      articlePagination: {
        filter: { status: 'published' },
        query: {},
        page: 1,
        limit: 6,
        lastPage: null
      },

      recommendationsOptions: [
        {
          label: this.$t('global:form.tracks.yes'),
          value: true
        },
        {
          label: this.$t('global:form.tracks.no'),
          value: false
        }
      ]
    }
  },

  computed: {
    previewImage () {
      if (this.formData.og_image && typeof this.formData.og_image === 'object') {
        return URL.createObjectURL(this.formData.og_image)
      }

      if (this.formData.og_image && typeof this.formData.og_image === 'string') {
        return this.formData.og_image
      }

      if (this.formData.og_image_resolved) {
        return this.formData.og_image_resolved
      }

      if (this.formData.cover_image && typeof this.formData.cover_image === 'string') {
        return this.formData.cover_image
      }

      return null
    },

    previewTitle () {
      return this.formData.seo_title || this.formData.title || this.$t('articles.social:preview.title.placeholder')
    },

    previewDescription () {
      return this.formData.meta_description || this.formData.summary || this.$t('articles.social:preview.description.placeholder')
    },

    previewDomain () {
      const portalUrl = process.env.VUE_APP_PORTAL_URL || ''

      return portalUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')
    },

    selectedArticles () {
      return this.formData.recommendations || []
    },

    canAddMore () {
      return this.selectedArticles.length < 3
    },

    availableResults () {
      const selectedIds = this.selectedArticles.map(a => a.recommendedId)
      const currentId = this.formData.id

      return this.publishedArticles.filter(article => {
        return !selectedIds.includes(article.id) && article.id !== currentId
      })
    }
  },

  watch: {
    'articlePagination.page' () {
      this.loadPublishedArticles()
    },

    'formData.id' () {
      this.loadPublishedArticles()
    },

    searchQuery () {
      this.articlePagination.page = 1
      this.articlePagination.query = this.searchQuery ? { title: this.searchQuery } : {}
      this.loadPublishedArticles()
    }
  },

  created () {
    this.loadPublishedArticles()
  },

  methods: {
    ...mapActions([ 'attemptGetArticlesList' ]),

    // eslint-disable-next-line vue/no-unused-properties -- called from parent via $refs.currentStep.validate()
    validate () {
      return true
    },

    emit (patch) {
      this.$emit('input', patch)
    },

    onOgImageInput (file) {
      this.emit({ og_image: file })
    },

    onRecommendationsEnabledInput (value) {
      this.emit({ recommendations_enabled: value })
    },

    onRemoveOgImage () {
      this.emit({
        og_image: null,
        og_image_resolved: this.formData.cover_image && typeof this.formData.cover_image === 'string'
          ? this.formData.cover_image
          : null
      })
    },

    async loadPublishedArticles () {
      const filter = { status: 'published' }

      if (this.formData.id) filter.exclude_id = this.formData.id

      try {
        const response = await this.attemptGetArticlesList({
          ...this.articlePagination,
          filter
        })

        this.publishedArticles = response.data || []
        this.articlePagination.lastPage = response.lastPage
      } catch {
        this.publishedArticles = []
      }
    },

    isRecommendationDisabled (recommendation) {
      return Boolean(recommendation.recommendedStatus) &&
        recommendation.recommendedStatus !== 'published'
    },

    addRecommendation (articleId) {
      if (!this.canAddMore) return

      const article = this.publishedArticles.find(a => a.id === articleId)

      if (!article) return

      const updated = [
        ...this.selectedArticles,
        {
          recommendedId: article.id,
          recommendedTitle: article.title,
          recommendedSummary: article.summary || '',
          recommendedImage: article.cardImage || article.coverImage || null,
          recommendedSlug: article.slug,
          recommendedLink: `/blog/${article.slug}`
        }
      ]

      this.emit({ recommendations: updated })
    },

    removeRecommendation (recommendedId) {
      const updated = this.selectedArticles.filter(r => r.recommendedId !== recommendedId)

      this.emit({ recommendations: updated })
    }
  }
}
</script>

<template>
  <form
    class="article-content-form"
    @submit.prevent
  >
    <FormSection :title="$t('articles.social:section.og')">
      <p class="article-section-subtitle">
        {{ $t('articles.social:section.og.subtitle') }}
      </p>

      <Upload
        :value="formData.og_image"
        icon="image-multiple"
        :label="$t('articles.social:og_image')"
        :description="$t('articles.social:og_image.description')"
        cropper
        :width="1200"
        :height="630"
        :preview="0.4"
        @input="onOgImageInput"
      />
      <p class="article-og-hint">
        {{ $t('articles.social:og_image.hint') }}
      </p>
      <Action
        v-if="formData.og_image"
        type="button"
        flat
        icon="delete"
        :text="$t('articles.social:og_image.remove')"
        @click="onRemoveOgImage"
      />
    </FormSection>

    <div class="article-social-previews">
      <div class="article-social-preview-col">
        <h4 class="article-social-preview-label">
          {{ $t('articles.social:preview.facebook') }}
        </h4>
        <div class="article-social-card article-social-card--facebook">
          <div class="article-social-card-image">
            <img
              v-if="previewImage"
              :src="previewImage"
              :alt="previewTitle"
            >
            <div
              v-else
              class="article-social-card-placeholder"
            >
              <Icon name="image-multiple" />
              <span>{{ $t('articles.social:preview.image.placeholder') }}</span>
            </div>
          </div>
          <div class="article-social-card-body">
            <span class="article-social-card-domain">{{ previewDomain }}</span>
            <h4 class="article-social-card-title">
              {{ previewTitle }}
            </h4>
            <p class="article-social-card-description">
              {{ previewDescription }}
            </p>
          </div>
        </div>
      </div>

      <div class="article-social-preview-col">
        <h4 class="article-social-preview-label">
          {{ $t('articles.social:preview.twitter') }}
        </h4>
        <div class="article-social-card article-social-card--twitter">
          <div class="article-social-card-image">
            <img
              v-if="previewImage"
              :src="previewImage"
              :alt="previewTitle"
            >
            <div
              v-else
              class="article-social-card-placeholder"
            >
              <Icon name="image-multiple" />
              <span>{{ $t('articles.social:preview.image.placeholder') }}</span>
            </div>
          </div>
          <div class="article-social-card-body">
            <h4 class="article-social-card-title">
              {{ previewTitle }}
            </h4>
            <p class="article-social-card-description">
              {{ previewDescription }}
            </p>
            <span class="article-social-card-domain">{{ previewDomain }}</span>
          </div>
        </div>
      </div>
    </div>

    <FormSection :title="$t('articles.social:section.recommendations')">
      <p class="article-section-subtitle">
        {{ $t('articles.social:section.recommendations.subtitle') }}
      </p>

      <Radio
        :value="formData.recommendations_enabled"
        :items="recommendationsOptions"
        horizontal
        @input="onRecommendationsEnabledInput"
      />

      <div
        v-if="formData.recommendations_enabled"
        class="article-recommendations"
      >
        <div class="search-field">
          <InputField
            v-model="searchQuery"
            :label="$t('articles.social:recommendations.search')"
          />
        </div>
        <div class="link-table">
          <div class="solutions-available">
            <Datatable
              :items="availableResults"
              hide-horizontal-line
            >
              <template slot="thead">
                <th
                  class="th"
                  width="300"
                >
                  {{ $t('articles.social:recommendations.available') }}
                </th>
                <th
                  class="th"
                  width="40"
                />
              </template>
              <template
                slot="tbody"
                slot-scope="props"
              >
                <tr>
                  <td class="td td-full">
                    <span class="td-text bolder">{{ props.item.title }}</span>
                  </td>
                  <td class="td td-button td-small text-right">
                    <Action
                      type="button"
                      icon="keyboard_arrow_right"
                      flat
                      :disabled="!canAddMore"
                      @click="addRecommendation(props.item.id)"
                    />
                  </td>
                </tr>
              </template>
            </Datatable>
            <Pagination
              :scroll-enabled="false"
              :active-page="articlePagination.page"
              :page-count="articlePagination.lastPage"
              @firstPage="articlePagination.page = 1"
              @lastPage="articlePagination.page = articlePagination.lastPage"
              @nextPage="articlePagination.page += 1"
              @previousPage="articlePagination.page > 1 ? articlePagination.page -= 1 : null"
            />
          </div>
          <Divisor orientation="vertical" />
          <div class="recommended-solutions">
            <Datatable
              :items="selectedArticles"
              hide-horizontal-line
            >
              <template slot="thead">
                <th class="th">
                  {{ $t('articles.social:recommendations.selected') }}
                </th>
                <th
                  class="th"
                  width="40"
                />
              </template>
              <template
                slot="tbody"
                slot-scope="props"
              >
                <tr>
                  <td class="td td-full">
                    <span
                      class="td-text bolder"
                      :class="{ 'recommendation-disabled': isRecommendationDisabled(props.item) }"
                    >
                      {{ props.item.recommendedTitle }}
                      <span
                        v-if="isRecommendationDisabled(props.item)"
                        class="recommendation-disabled-badge"
                      >{{ $t('articles.social:recommendations.disabled') }}</span>
                    </span>
                  </td>
                  <td class="td td-button td-small text-right">
                    <Action
                      type="button"
                      icon="close"
                      flat
                      @click="removeRecommendation(props.item.recommendedId)"
                    />
                  </td>
                </tr>
              </template>
            </Datatable>
          </div>
        </div>
      </div>
    </FormSection>
  </form>
</template>
